import { apiUrl } from '@algoan/dashboard/env';
import { AsyncThunk } from '@algoan/dashboard/store/store';
import {
  Analysis,
  AnalysisStatus,
  CashFlow,
  GroupedCashFlow,
  PaginatedResources,
  TransactionCategory,
  NotificationVariant,
  AnalysesErrorMessages,
  AnalysisConfigurationMode,
  AnalysisParameters,
  Essentials,
} from '@algoan/dashboard/model';
import {
  fetchAnalysesStart,
  fetchAnalysesSuccess,
  fetchAnalysesError,
  resetAnalysesState,
  recategorisationError,
  recategorisationStart,
  recategorisationSuccess,
  fetchEssentialsStart,
  fetchEssentialsSuccess,
  fetchEssentialsError,
  refreshFinish,
  refreshStart,
} from '@algoan/dashboard/store/slice';
import { mapAnalysisErrorCodeToAnalysisMessage } from '@algoan/dashboard/store/utils';
import { buildUrl } from '@algoan/dashboard/utils';
import { authRequest } from './auth-thunks';
import { AmplitudeEvent, logAmplitudeEvent } from '@algoan/dashboard/analytics';
import { analysisPageCancellationToken, fetchNewAggregationData } from './dashboard-thunks';
import { batch } from 'react-redux';
import { notify } from './app-state-thunks';
import { analysisMessages } from '@algoan/dashboard/store/messages';
import { format } from 'date-fns';

export const fetchAnalyses =
  (customerId: string): AsyncThunk<void> =>
  async (dispatch) => {
    const analysesUrl = buildUrl(`${apiUrl}/v2/customers/${customerId}/analyses`, [
      {
        key: 'limit',
        value: 1,
      },
      {
        key: 'filter',
        value: { status: AnalysisStatus.COMPLETED },
      },
      {
        key: 'sort',
        value: { createdAt: -1 },
      },
    ]);

    try {
      batch(() => {
        dispatch(resetAnalysesState());
        dispatch(fetchAnalysesStart());
      });
      const { data } = await dispatch(
        authRequest<PaginatedResources<Analysis>>('GET', analysesUrl, undefined, {
          cancelToken: analysisPageCancellationToken?.token,
        }),
      );

      const { resources } = data;

      if (resources.length === 0) {
        const analysesWithErrorUrl = buildUrl(`${apiUrl}/v2/customers/${customerId}/analyses`, [
          {
            key: 'limit',
            value: 1,
          },
          {
            key: 'filter',
            value: { status: AnalysisStatus.ERROR },
          },
          {
            key: 'sort',
            value: { createdAt: -1 },
          },
        ]);
        const { data } = await dispatch(
          authRequest<PaginatedResources<Analysis>>('GET', analysesWithErrorUrl, undefined, {
            cancelToken: analysisPageCancellationToken?.token,
          }),
        );

        const analysisWithError: Analysis | undefined = data.resources[0];

        if (!analysisWithError) {
          dispatch(fetchAnalysesError({ error: AnalysesErrorMessages.NO_ANALYSES }));
          return;
        }

        const isZeroIncomeOrLowActivityScoreErrors =
          analysisWithError.error?.code === 'ZERO_INCOME' || analysisWithError.error?.code === 'LOW_ACTIVITY_SCORE';

        if (isZeroIncomeOrLowActivityScoreErrors) {
          dispatch(fetchAnalysesSuccess({ analyses: [analysisWithError] }));
        }

        dispatch(
          fetchAnalysesError({
            error: mapAnalysisErrorCodeToAnalysisMessage(analysisWithError.error?.code),
            date: analysisWithError.updatedAt,
            errorMessage: analysisWithError.error?.message,
          }),
        );
        return;
      }

      dispatch(fetchAnalysesSuccess({ analyses: resources }));
    } catch (error) {
      dispatch(fetchAnalysesError({ error: AnalysesErrorMessages.TECHNICAL_ERROR }));
    }
  };

export const recategorise =
  (
    customerId: string,
    analysisId: string,
    cashFlowToUpdate: GroupedCashFlow | CashFlow,
    newCategory: TransactionCategory,
  ): AsyncThunk<void> =>
  async (dispatch) => {
    try {
      dispatch(recategorisationStart());
      const cashFlowUpdateUrl = `${apiUrl}/v2/customers/${customerId}/analyses/${analysisId}/credit-insights/cash-flows/${cashFlowToUpdate.id}`;
      await dispatch(authRequest<CashFlow[]>('post', cashFlowUpdateUrl, { category: newCategory }));
      dispatch(recategorisationSuccess());
      logAmplitudeEvent(AmplitudeEvent.CASHFLOW_SUCCESS);
    } catch (error) {
      dispatch(recategorisationError());
      logAmplitudeEvent(AmplitudeEvent.CASHFLOW_ERROR);
      throw error;
    }
  };

export const changeAnalysisConfiguration =
  ({
    customerId,
    analyzedTransactionsPeriodStart,
    analyzedTransactionsPeriodEnd,
  }: {
    customerId: string;
    analyzedTransactionsPeriodStart: Date | null;
    analyzedTransactionsPeriodEnd: Date | null;
  }): AsyncThunk<void> =>
  async (dispatch) => {
    try {
      dispatch(refreshStart());
      if (!analyzedTransactionsPeriodStart || !analyzedTransactionsPeriodEnd) {
        throw new Error('Invalid date range for analysis configuration');
      }

      const changeAnalysisModeUrl = `${apiUrl}/v2/customers/${customerId}/analyses`;
      const analyzedTransactionsPeriodStartFormatted = format(analyzedTransactionsPeriodStart, 'yyyy-MM-dd');
      const analyzedTransactionsPeriodEndFormatted = format(analyzedTransactionsPeriodEnd, 'yyyy-MM-dd');

      const parameters = {
        parameters: {
          period: {
            mode: AnalysisConfigurationMode.CUSTOM_WINDOW,
            customWindowDateRange: {
              minDate: analyzedTransactionsPeriodStartFormatted,
              maxDate: analyzedTransactionsPeriodEndFormatted,
              minScoreDate: analyzedTransactionsPeriodStartFormatted,
              maxScoreDate: analyzedTransactionsPeriodEndFormatted,
            },
          },
        },
      };

      const { data } = await dispatch(authRequest<{ id: string }>('post', changeAnalysisModeUrl, parameters));
      await dispatch(fetchNewAggregationData(customerId, data.id));
      logAmplitudeEvent(AmplitudeEvent.NEW_WINDOW_WIDGET_COMPLETED, {
        windowSelected: AnalysisConfigurationMode.CUSTOM_WINDOW,
      });
      dispatch(
        notify({
          type: NotificationVariant.SUCCESS,
          message: analysisMessages.changeAnalysisModeSuccess,
        }),
      );
    } catch (error) {
      dispatch(
        notify({
          type: NotificationVariant.ERROR,
          message: analysisMessages.changeAnalysisModeError,
        }),
      );
    } finally {
      dispatch(refreshFinish());
    }
  };

export const refreshAnalysis =
  (customerId: string): AsyncThunk<void> =>
  async (dispatch) => {
    try {
      dispatch(refreshStart());

      const refreshAnalysisUrl = `${apiUrl}/v2/customers/${customerId}/analyses`;
      const { data } = await dispatch(authRequest<{ id: string }>('post', refreshAnalysisUrl, { accounts: [] }));
      await dispatch(fetchNewAggregationData(customerId, data.id));

      dispatch(
        notify({
          type: NotificationVariant.SUCCESS,
          message: analysisMessages.refreshAnalysisSuccess,
        }),
      );
    } catch (error) {
      dispatch(
        notify({
          type: NotificationVariant.ERROR,
          message: analysisMessages.refreshAnalysisError,
        }),
      );
      throw error;
    } finally {
      dispatch(refreshFinish());
    }
  };

export const changeAnalysisMode =
  (customerId: string, parameters: AnalysisParameters): AsyncThunk<void> =>
  async (dispatch) => {
    try {
      dispatch(refreshStart());

      const changeAnalysisModeUrl = `${apiUrl}/v2/customers/${customerId}/analyses`;
      const { data } = await dispatch(
        authRequest<{ id: string }>('post', changeAnalysisModeUrl, { parameters: parameters }),
      );
      await dispatch(fetchNewAggregationData(customerId, data.id));
      logAmplitudeEvent(AmplitudeEvent.NEW_WINDOW_WIDGET_COMPLETED, { windowSelected: parameters.period?.mode });
      dispatch(
        notify({
          type: NotificationVariant.SUCCESS,
          message: analysisMessages.changeAnalysisModeSuccess,
        }),
      );
    } catch (error) {
      dispatch(
        notify({
          type: NotificationVariant.ERROR,
          message: analysisMessages.changeAnalysisModeError,
        }),
      );
    } finally {
      dispatch(refreshFinish());
    }
  };

export const fetchEssentials =
  (customerId: string, analysisId: string): AsyncThunk<void> =>
  async (dispatch) => {
    const essentialsUrl = `${apiUrl}/v2/customers/${customerId}/analyses/${analysisId}/essentials`;

    try {
      dispatch(fetchEssentialsStart());
      const { data } = await dispatch(
        authRequest<Essentials>('GET', essentialsUrl, undefined, {
          cancelToken: analysisPageCancellationToken?.token,
        }),
      );
      dispatch(fetchEssentialsSuccess({ essentials: data }));
    } catch (error) {
      dispatch(fetchEssentialsError());
    }
  };
